<template>
  <SearchInput
    ref="targetRef"
    v-model="filterText"
    role="search"
    placeholder="请输入搜索"
    :size="size"
    v-bind="$attrs"
    @focus="handleFocus"
    @blur="handleBlur"
    @search="handleSearch"
  />
  <div>
    <BodyPortal :active="fixed">
      <SelectBox
        v-if="isActive"
        ref="popperRef"
        :filter-text="filterText"
        :empty="items.length < 1"
        :allow-custom="false"
        :input-mode="inputMode"
        :status="statusHook"
        :bs="popperBs"
        @click.stop="() => {}"
      >
        <template #empty>
          <slot name="empty" />
        </template>
        <slot
          :items="items"
          :handleSelect="handleSelect"
        >
          <div>
            <Option
              v-for="o of items"
              :key="o.id"
              :bs="{
                py: size === 'l' ? 13 : 9,
              }"
              @mousedown="e => handleSelect(o)"
            >
              <template v-if="needHighLight && filterText">
                <template
                  v-for="(words, index) of o.text.split(value)"
                  :key="`${index}-value`"
                >
                  <NewBsBox
                    v-if="index % 2 === 1"
                    :bs="{ color: 'mute' }"
                    as="span"
                  >
                    {{ value }}
                  </NewBsBox>
                  <span>
                    {{ words }}
                  </span>
                </template>
              </template>
              <template v-else>
                {{ o.text }}
              </template>
            </Option>
          </div>
        </slot>
      </SelectBox>
    </BodyPortal>
  </div>
</template>

<script lang="ts">
  import {
    computed, PropType, defineComponent,
  } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import Option from '@xhs/yam-beer/cmp/overlayers/Option/Option.vue'
  import SearchInput from './SearchInput.vue'
  import SelectBox from '../Select/SelectBox.vue'
  import useOptions, {
    ExtendItem,
    UseOptionsParams,
    GetListFn,
  } from '../../../compose/options/useOptions'
  import { InputSize } from '../InputGroup/InputGroup.vue'
  import initStyle from './style'
  import { useSelectPopover, SelectPopoverPartial } from '../../../compose/popover'
  import { BeerStyleObj } from '../../../core'
  import BodyPortal from '../../primitive/BodyPortal'

  initStyle()

  /**
   * 即时输入，即时调用 API 提示选项来选择
   *
   * 注意拉，大部分搜索场景，你可能直接用 SearchInput 就够拉
   * - input + table => SearchInput
   * - input + drowdown => Search
   *
   * 默认 debounce 300ms 来触发搜索
   *
   * @see 继承SearcInput #sfc-SearchInput
   */
  export default defineComponent({
    name: 'Search',
    components: {
      SearchInput,
      SelectBox,
      Option,
      NewBsBox,
      BodyPortal,
    },
    inheritAttrs: false,
    props: {
      getList: {
        type: Function as PropType<GetListFn>,
        required: false,
      },
      /**
       * 大小样式,
       * @type {l|m}
       */
      size: {
        type: String as PropType<InputSize>,
        default: 'm',
      },
      /**
       * 是否禁止
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * 确认按钮的 Bs
       */
      actionBs: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 选项是否样式高亮匹配的搜索文字
       */
      needHighLight: {
        type: Boolean,
        default: false,
      },
      /**
       * useOption 的自定义选项
       */
      optionParams: {
        type: Object as PropType<UseOptionsParams>,
        default: () => ({}),
      },
      /**
       * 下拉框的高级选项
       * @see Popover /beer-next/ui-blocks/overlayers/popover-doc
       */
      popoverParams: {
        type: Object as PropType<SelectPopoverPartial>,
        default: () => ({}),
      },
      popperBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否启用 Body Portal
      */
      fixed: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否空字符串触发搜索
       */
      searchOnEmpty: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否 reset 空字符串的搜索 API
       */
      refreshOnEmpty: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      /**
       * 点击选项事件
       */
      'click:item': null,
      /**
       * 触发搜索事件
       */
      search: null,
    },
    setup(props, { emit }) {
      const optionsHook = useOptions({
        options: [],
        getList: props.getList,
        notGetOnEmpty: !props.searchOnEmpty,
        refreshOnEmpty: props.refreshOnEmpty,
        ...props.optionParams,
      })

      const {
        targetRef,
        popperRef,
        isActive,
        toggle,
      } = useSelectPopover({
        ...props.popoverParams,
        disabled: computed(() => props.disabled),
      })
      const handleClear = () => {
        optionsHook.filterText.value = ''
        toggle(false)
      }
      const handleSelect = (o: ExtendItem) => {
        toggle(false)
        emit('click:item', o.raw)
      }
      const handleSearch = () => {
        emit('search')
        optionsHook.statusHook.exec()
      }
      return {
        targetRef,
        popperRef,
        isActive,
        inputMode: optionsHook.inputMode,
        statusHook: optionsHook.statusHook,
        items: optionsHook.items,
        filterText: optionsHook.filterText,
        handleClear,
        optionsHook,
        handleSelect,
        handleSearch,
        handleBlur: () => {
          if (!optionsHook.filterText.value) {
            toggle(false)
          }
        },
        handleFocus: () => {
          toggle(true)
        },
      }
    },
  })
</script>
